import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

/**
 * UploadFilesTool - Handles uploading multiple individual files to a target directory
 * Files are uploaded directly to the target path without creating subfolders
 */
export class UploadFilesTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'uploadFilesTool';
    this.name = "uploadFiles";
    this.description = "Upload multiple individual files to a target directory";
    this.parameters = {
      type: "object",
      properties: {
        files: {
          type: "array",
          description: "Array of file objects with name, content, and targetPath",
          items: {
            type: "object",
            properties: {
              name: { type: "string", description: "File name" },
              content: { type: "string", description: "File content as string" },
              targetPath: { type: "string", description: "Target path in workspace" },
              encoding: { type: "string", description: "Content encoding (utf8 or base64)" }
            },
            required: ["name", "content", "targetPath"]
          }
        }
      },
      required: ["files"]
    };
  }

  async execute({ files }) {
    try {
      logger.log({
        tags: "workspace|tool|upload-files",
        color1: "blue"
      }, `UploadFilesTool: Processing ${files.length} files`);

      const results = [];

      for (const file of files) {
        try {
          // Use writeFileTool to save each file
          const writeResult = await this.toolSystem.writeFileTool.writeFile(
            file.targetPath,
            file.content,
            false, // overwrite - don't overwrite existing files
            file.encoding || 'utf8',
            true // createDirectories
          );

          if (writeResult.success) {
            results.push({
              file: file.name,
              path: file.targetPath,
              success: true
            });
            logger.log({
              tags: "workspace|tool|upload-files",
              color1: "green"
            }, `Uploaded: ${file.name} → ${file.targetPath}`);
          } else {
            results.push({
              file: file.name,
              path: file.targetPath,
              success: false,
              error: writeResult.message || writeResult.error
            });
            logger.log({
              tags: "workspace|tool|upload-files",
              color1: "red"
            }, `Failed to upload ${file.name}: ${writeResult.message || writeResult.error}`);
          }
        } catch (fileError) {
          results.push({
            file: file.name,
            path: file.targetPath,
            success: false,
            error: fileError.message
          });
          logger.log({
            tags: "workspace|tool|upload-files",
            color1: "red"
          }, `Error uploading ${file.name}: ${fileError.message}`);
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;

      return {
        success: failureCount === 0,
        data: {
          results,
          summary: {
            total: files.length,
            successful: successCount,
            failed: failureCount
          }
        },
        message: `Uploaded ${successCount} of ${files.length} files${failureCount > 0 ? ` (${failureCount} failed)` : ''}`
      };

    } catch (error) {
      logger.log({
        tags: "workspace|tool|upload-files",
        color1: "red"
      }, `UploadFilesTool error: ${error.message}`);

      return {
        success: false,
        error: error.message,
        message: "Failed to upload files"
      };
    }
  }
}